import { useEffect, useState } from "react";
import type { ReviewItem } from "../../shared/review-templates";
import type { ApprovalDTO } from "../../shared/types";
import { useUser } from "../App";
import { canCheckSend, canSettleRelease, releaseState } from "../lib/approval-text";
import { AgentAvatar } from "./AgentAvatar";
import { BrandIcon } from "./BrandIcon";
import { SlackMarkdown } from "./SlackMarkdown";
import { TextLink } from "./TextLink";
import { Avatar, Badge, timeAgo } from "./ui";
import { LockedVerdicts, ReleaseOutcome, VerdictBar } from "./Verdicts";

const part = (item: ReviewItem, field: string, key: string): string => {
  const value = item[field];
  return value && typeof value === "object" ? (value[key] ?? "") : "";
};

/** "14:05" today, "21 Oct, 14:05" another day: when it went, as Slack would say it. */
function sentAt(iso: string | null): string {
  if (!iso) return "";
  const at = new Date(iso);
  const time = at.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return at.toDateString() === new Date().toDateString() ? time : `${at.toLocaleDateString([], { day: "numeric", month: "short" })}, ${time}`;
}

/**
 * A Slack message an agent wants to send as the person it works for, decided in the chat it was filed in: where it
 * goes, the thread it answers, the message as Slack will show it, why — and Approve, Request changes (typed or
 * spoken) or Reject for that person alone. Once decided, one line says what became of it.
 */
export function SlackMessageCard({ approval, canDecide, onDecided }: { approval: ApprovalDTO; canDecide: boolean; onDecided?: (updated: ApprovalDTO) => void }) {
  // What we just decided holds until the server's own copy arrives (the websocket, or the next load).
  const [local, setLocal] = useState<ApprovalDTO | null>(null);
  useEffect(() => setLocal(null), [approval]);
  const a = local ?? approval;
  const user = useUser();
  const keep = (updated: ApprovalDTO) => {
    setLocal(updated);
    onDecided?.(updated);
  };
  const item = (a.payload ?? {}) as ReviewItem;
  // The name is Slack's own, read by the platform when it was filed; the id beside it is where it really goes.
  const to = part(item, "to", "name");
  const toId = part(item, "to", "id");
  const thread = part(item, "thread", "first_line");
  const message = typeof item.message === "string" ? item.message : "";
  const why = typeof item.why === "string" ? item.why : "";
  const asWho = a.ownerUserId === user.id ? "you" : (a.ownerName ?? "the person it is for");

  return (
    <div className="card border-l-2 border-l-amber-400/70 p-5 md:p-6">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
        <AgentAvatar agent={a.agentId} name={a.agentName} size="sm" />
        <Badge tone="amber">Slack message</Badge>
        {a.version > 1 && <Badge tone="zinc">version {a.version}</Badge>}
        <span className="text-muted">
          <span className="font-medium text-fg">{a.agentName}</span> wants to send this as {asWho}
        </span>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
        <BrandIcon brand="slack" className="size-4 shrink-0" />
        <span className="text-muted">To</span>
        <span className="font-medium break-words text-fg">{to}</span>
        <span className="font-mono text-xs break-all text-faint">{toId}</span>
      </div>
      {thread && <p className="mt-1 text-sm break-words text-muted">In the thread: “{thread}”</p>}
      <div className="mt-3 flex gap-3 rounded-xl border border-line bg-surface p-3">
        <Avatar name={a.ownerName ?? "?"} />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-fg">{a.ownerName ?? "You"}</p>
          <SlackMarkdown text={message} />
        </div>
      </div>
      {why && <p className="mt-2 text-sm break-words text-muted">Why: {why}</p>}
      {a.status === "pending" ? (
        <div className="mt-4">{canDecide ? <VerdictBar a={a} reviseLabel="Request changes" voice onDecided={(updated) => updated && keep(updated)} /> : <LockedVerdicts a={a} reviseLabel="Request changes" />}</div>
      ) : (
        <SlackOutcome a={a} to={to} canAct={canSettleRelease(a, user)} canCheck={canCheckSend(a, user)} onChanged={keep} />
      )}
    </div>
  );
}

/** What became of it, in one line: sent (with Slack's link), being sent, not sent and why, rejected, or sent back. */
function SlackOutcome({ a, to, canAct, canCheck, onChanged }: { a: ApprovalDTO; to: string; canAct: boolean; canCheck: boolean; onChanged: (updated: ApprovalDTO) => void }) {
  const state = releaseState(a);
  const by = a.decidedByName ? ` · ${a.decidedByName} · ${timeAgo(a.decidedAt)}` : ` · ${timeAgo(a.decidedAt)}`;
  return (
    <div className="mt-4 border-t border-line pt-3 text-sm">
      {a.status === "approved" && state === "sent" ? (
        <p className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-muted">
          <span className="text-success">✓</span>
          <span className="font-medium text-fg">Sent to {to}</span>
          <span className="text-faint">· {sentAt(a.usedAt)}</span>
          {a.usedLink && (
            <>
              <span className="text-faint">·</span>
              <TextLink href={a.usedLink}>Open in Slack</TextLink>
            </>
          )}
        </p>
      ) : a.status === "approved" && !state && a.releaseStartedAt ? (
        <p className="text-muted">Sending to {to}…</p>
      ) : a.status === "approved" ? (
        <>
          <p className="text-muted">
            <span className="font-medium text-fg">Approved</span>
            <span className="text-faint">{by}</span>
          </p>
          <ReleaseOutcome a={a} canAct={canAct} canCheck={canCheck} onChanged={onChanged} />
        </>
      ) : a.status === "revision_requested" ? (
        <p className="break-words text-muted">
          <span className="text-faint">↻ </span>
          <span className="font-medium text-fg">Changes requested:</span> {a.decisionNote}
        </p>
      ) : (
        <>
          <p className="text-muted">
            <span className="text-faint">{a.status === "withdrawn" ? "– " : "✕ "}</span>
            <span className="font-medium text-fg">{a.status === "withdrawn" ? "Withdrawn" : "Rejected"}</span>
            <span className="text-faint">{by}</span>
          </p>
          {a.decisionNote && <p className="mt-1 break-words whitespace-pre-wrap text-muted">“{a.decisionNote}”</p>}
        </>
      )}
    </div>
  );
}
